import {queryOptions, type UseQueryOptions} from '@tanstack/react-query';
import {GOTCHA_QUERY_KEY_MAP} from '../api/gotcha/constant';
import {postGotchaGet} from '../api/gotcha/postGotchaGet';
import {postGotchaGetWeb} from '../api/gotcha/postGotchaGetWeb';
import type {FetcherOptions} from '../axios/axiosBffFetch';
import {PagewebError} from '../errors/PagewebError.class';
import {createGotchaInfo} from '../graphql/factory/GotchaInfo';
import type {
  GotchaQuery,
  GotchaQueryVariables,
} from '../graphql/generated/types';

/**
 * Gotcha 도메인 Query Factory
 *
 * Adapter layer를 제거하고 Factory에서 직접 데이터 변환을 수행
 */
export const gotchaFactory = {
  /**
   * 갓챠 정보 조회
   *
   * ⚠️ Resolver 로직:
   * - hasMapUid에 따라 다른 API 호출 (v3/v2)
   * - response.gotcha_info와 response.gotcha_id를 병합하여 factory로 전달
   * - createGotchaInfo factory로 변환
   */
  gotcha: (
    variables: GotchaQueryVariables,
    options?: Omit<
      UseQueryOptions<GotchaQuery, PagewebError, GotchaQuery>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: GOTCHA_QUERY_KEY_MAP.gotcha(variables),
      queryFn: async () => {
        const {gotchaId, adLocId, hasMapUid} = variables;

        const response = hasMapUid
          ? await postGotchaGetWeb({gotchaId, adLocId}, fetcherOptions)
          : await postGotchaGet({gotchaId, adLocId}, fetcherOptions);

        // Adapter의 combine 로직을 여기서 수행
        return {
          gotcha: createGotchaInfo({
            ...response.gotcha_info,
            gotcha_id: response.gotcha_id,
          }),
        };
      },
      ...options,
    }),
} as const;
